/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { LogItem } from "./LogsData";
import { FilterByAttribute, LogRowDetails, logRowJson } from "./LogRowDetails";
import { getCTXRlistGlobal, getLogsByCtxrId } from "./request/request";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import LogsTimeRangePicker, {
  PresetKey,
  resolvePreset,
  TimeRange,
  TimeRangeMode,
} from "ctxReflectorV2/components/Logs/LogsTimeRangePicker";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";

import { useAppSelector } from "@redux/hooks";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";
import GenericCard from "@ui-components/Card/GenericCard";
import { ReflectorLogsTable } from "ctxReflectorV2/components/Logs/LogsTable/ReflectorLogsTable";
import { LogsTableToolbar } from "ctxReflectorV2/components/Logs/LogsTable/LogsTableToolbar";
import { ExcludeFilter } from "ctxReflectorV2/components/Logs/LogsTable/ExcludeFilter";
import { useLogsView } from "ctxReflectorV2/components/Logs/LogsTable/useLogsView";
import {
  fromV1List,
  ReflectorLogRecord,
} from "ctxReflectorV2/components/Logs/LogsTable/logRecord";
import { CTXR_FIELD_LABEL, makeLogFields, TIMESTAMP_FIELD_LABEL } from "./logFieldSpec";
import { formatLogTimestamp } from "./logTimestamp";
import {
  FILTER_ROW_EXCLUDE_CLASS,
  FILTER_ROW_INSTANCE_WIDTH,
  FILTER_ROW_SEARCH_CLASS,
  FILTER_ROW_SEVERITY_WIDTH,
} from "ctxReflectorV2/components/Logs/logsFilterRow";
import { LogsVolumeHistogram } from "ctxReflectorV2/components/Logs/LogsVolume/LogsVolumeHistogram";
import type { RawVolumeBucket } from "ctxReflectorV2/components/Logs/LogsVolume/volume";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";

// Same picker as Reflector V2 Logs (CTX-1337); V1 opens on the last 24 hours,
// which is what the old dashboard's range list led with.
const DEFAULT_RANGE_PRESET: PresetKey = "24h";

/** The handler's fixed page size: a full page is the only sign there may be more. */
const LOGS_PAGE_SIZE = 300;

interface LogsPageResult {
  items: LogItem[];
  /** `nextSortValues` of the page, only when a further page may exist. */
  nextCursor: unknown;
  /** First page only. Null until chopper-backend CTX-1549 is deployed. */
  histogram: RawVolumeBucket[] | null;
  /** First page only; a number once CTX-1549 is deployed, the capped `{relation, value}` before. */
  total: number | null;
}

interface CtxrItem {
  id: string;
  name: string;
  description: string;
  subscriptionId: number;
  Suscription?: string;
}

export function LogsPage() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isFetching, setIsFetching] = useState(false);
  const [tablesData, setTablesData] = useState<LogItem[]>([]);
  // Where the next page starts; null once the range has no more lines to load.
  const [nextCursor, setNextCursor] = useState<unknown>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Failure of the next page only: the loaded rows stay and the toolbar offers Retry.
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  // Volume block and exact total of the range; both come with the first page only.
  const [histogram, setHistogram] = useState<RawVolumeBucket[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  // Answers of an earlier query (other filters, range or CTX-R) must not land in this one.
  const requestSeq = useRef(0);

  // CTXR
  const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
  const [firstOptions, setFirstOptions] = useState<string[]>([]);
  const [selectedFirst, setSelectedFirst] = useState("");

  // The attribute search's chips live here so an expanded row's filter buttons
  // can add one (CTX-1356). They filter the loaded rows on the client.
  const [attributeChips, setAttributeChips] = useState<ParsedFilter[]>([]);

  // Each attribute parsed out of the loaded rows is a field, so it is
  // suggested and its chip filters.
  const logFields = useMemo(
    () => makeLogFields(selectedFirst, tablesData),
    [selectedFirst, tablesData]
  );
  const [simpleSearchValue, setSimpleSearchValue] = useState("");

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return logFields.map((f) => f.label);
  }, [logFields]);

  // Severity
  const severityOptions = ["All Levels", "ERROR", "DEBUG", "INFO", "WARNING"];
  const [selectedSeverity, setSelectedSeverity] = useState("All Levels");

  // Rango
  const [range, setRange] = useState<TimeRange>(() =>
    resolvePreset(DEFAULT_RANGE_PRESET)
  );
  const [rangeMode, setRangeMode] = useState<TimeRangeMode>({
    kind: "preset",
    key: DEFAULT_RANGE_PRESET,
  });

  // Helper function to find CTX-R object by display name
  const findCtxrByDisplayName = useCallback(
    (displayName: string): CtxrItem | undefined => {
      return fullCTXRlist.find(
        (obj) => `${obj.name} (${obj.description})` === displayName
      );
    },
    [fullCTXRlist]
  );

  // Helper function to map API response to LogItem
  const mapLogsDataToTable = useCallback((data: any[]): LogItem[] => {
    return data.map((item) => ({
      timestamp: formatLogTimestamp(item.timestamp || ""),
      rawTimestamp: item.timestamp || "",
      level: item.level || "",
      message: item.log || "",
    }));
  }, []);

  // One page of the current query: the first one, or the one after `cursor`.
  // Null when there is no CTX-R to ask for.
  const requestPage = useCallback(
    async (cursor?: unknown): Promise<LogsPageResult | null> => {
      const foundObj = findCtxrByDisplayName(selectedFirst);
      if (!foundObj) {
        console.warn("No CTXR found for", selectedFirst);
        return null;
      }

      const token = await getAccessTokenSilently();

      const realLevel =
        selectedSeverity === "All Levels" ? undefined : selectedSeverity;
      const trimmedSearch = simpleSearchValue.trim();
      const realSearch = trimmedSearch === "" ? undefined : trimmedSearch;

      const resp = await getLogsByCtxrId(
        token,
        foundObj.id,
        tenantId,
        range,
        realLevel,
        realSearch,
        cursor
      );

      const hits = resp.data?.hits;
      const items = Array.isArray(hits) ? mapLogsDataToTable(hits) : [];
      // `total` is capped by the backend, so it says nothing about how many
      // lines remain: a full page with a cursor is the only evidence of more.
      const next = resp.data?.nextSortValues;
      const mayHaveMore =
        items.length === LOGS_PAGE_SIZE && next !== undefined && next !== null;
      const rawTotal = resp.data?.total;
      const rawHistogram = resp.data?.histogram;
      return {
        items,
        nextCursor: mayHaveMore ? next : null,
        histogram: Array.isArray(rawHistogram) ? rawHistogram : null,
        total: typeof rawTotal === "number" ? rawTotal : null,
      };
    },
    [
      range,
      selectedFirst,
      selectedSeverity,
      simpleSearchValue,
      tenantId,
      getAccessTokenSilently,
      findCtxrByDisplayName,
      mapLogsDataToTable,
    ]
  );

  // Centralized function to fetch the first page of logs
  const fetchLogsData = useCallback(
    async (skipLoadingState = false) => {
      const seq = ++requestSeq.current;
      setIsLoadingMore(false);
      try {
        if (!skipLoadingState) {
          setIsFetching(true);
        }

        const page = await requestPage();
        if (!page || seq !== requestSeq.current) return;

        setTablesData(page.items);
        setNextCursor(page.nextCursor);
        setHistogram(page.histogram);
        setTotal(page.total);
      } catch (err) {
        console.error("Error fetching logs =>", err);
      } finally {
        if (!skipLoadingState && seq === requestSeq.current) {
          setIsFetching(false);
        }
      }
    },
    [requestPage]
  );

  // Adds the next page to what is loaded, without unmounting the table.
  const loadMore = useCallback(async () => {
    if (nextCursor === null || isLoadingMore) return;
    const seq = requestSeq.current;
    try {
      setIsLoadingMore(true);
      setLoadMoreError(null);
      const page = await requestPage(nextCursor);
      if (!page || seq !== requestSeq.current) return;

      setTablesData((loaded) => [...loaded, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setLoadMoreError(parseAxiosError(err).message);
    } finally {
      if (seq === requestSeq.current) {
        setIsLoadingMore(false);
      }
    }
  }, [nextCursor, isLoadingMore, requestPage]);

  // A new query invalidates what is loaded and its cursor right away, not
  // after the debounce: Load more must never continue another query's pages.
  useEffect(() => {
    requestSeq.current += 1;
    setNextCursor(null);
    setIsLoadingMore(false);
    setLoadMoreError(null);
  }, [tenantId, selectedFirst, selectedSeverity, simpleSearchValue, range]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    // If tenantId can be 0 for global, don't use !tenantId
    if (tenantId === null || tenantId === undefined) return;

    let cancelled = false;

    const loadCtxrList = async () => {
      setIsFetching(true);
      try {
        const token = await getAccessTokenSilently();
        const resp = await getCTXRlistGlobal(-1, tenantId, token);
        const dataArray: CtxrItem[] = resp.data || [];

        if (cancelled) return;

        // Reset data when tenant changes / first loads
        setTablesData([]);
        setFullCTXRlist(dataArray);

        const mapped = dataArray.map(
          (item) => `${item.name} (${item.description})`
        );
        setFirstOptions(mapped);

        // Auto-select first CTX-R if available, otherwise clear selection
        if (mapped.length > 0) {
          setSelectedFirst(mapped[0]);
        } else {
          setSelectedFirst("");
        }

      } catch (err) {
        console.error("Error loading CTX-R list =>", err);
      } finally {
        if (!cancelled) {
          setIsFetching(false);
        }
      }
    };

    loadCtxrList();

    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated, tenantId, getAccessTokenSilently]);

  const handleSelectFirst = useCallback((val: string) => {
    setSelectedFirst(val);
  }, []);

  const handleRangeChange = (next: TimeRange, mode: TimeRangeMode) => {
    setRange(next);
    setRangeMode(mode);
  };

  // A preset is re-resolved against "now" so "Last 1 hour" stays the last hour;
  // the new range re-triggers the fetch effect. A custom range is re-fetched as is.
  const refreshRange = () => {
    if (rangeMode.kind === "preset") {
      setRange(resolvePreset(rangeMode.key));
    } else {
      fetchLogsData(false);
    }
  };

  // Auto-refresh when filters change (with debouncing)
  useEffect(() => {
    // Basic guards
    if (isLoading || !isAuthenticated) return;

    // If tenantId can be 0 for global, do NOT use !tenantId
    if (tenantId === null || tenantId === undefined) return;

    if (!selectedFirst || !fullCTXRlist.length) return;

    // Clear previous debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the request
    debounceTimerRef.current = setTimeout(() => {
      fetchLogsData(false);
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    tenantId,
    selectedFirst,
    selectedSeverity,
    simpleSearchValue,
    range,
    fullCTXRlist.length,
    isLoading,
    isAuthenticated,
    fetchLogsData,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Build maps for filter normalization (similar to MainPort)
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<LogItem>>();
    for (const f of logFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [logFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of logFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [logFields]);

  // Normalize filters (similar to MainPort)
  const normalizedFilters = useMemo(() => {
    return attributeChips.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [attributeChips, keyMap, fieldById]);

  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<LogItem>(normalizedFilters, logFields, { ipv4WholeToken: true }),
    [normalizedFilters, logFields]
  );

  const records = useMemo(() => fromV1List(tablesData), [tablesData]);
  const matchesAttributes = useCallback(
    (record: ReflectorLogRecord<LogItem>) => predicate(record.raw),
    [predicate]
  );
  const logsView = useLogsView(records, matchesAttributes);

  // Search Log is the server's free-text search over the whole range; its chip
  // is the `searchTerm`. Attribute filters live in the table's own search.
  const handleSearchLogFiltersChange = useCallback((filters: ParsedFilter[]) => {
    setSimpleSearchValue(filters[0]?.value ?? "");
  }, []);

  // The attribute search filters by what is loaded; its callback has nothing to
  // report since the chips are already this component's state.
  const ignoreAttributeFiltersChange = useCallback(() => undefined, []);

  // Contains, like a typed `attribute: value`; the same chip twice adds nothing.
  const filterByAttribute = useCallback<FilterByAttribute>((label, value) => {
    setAttributeChips((chips) =>
      chips.some((chip) => chip.key === label && chip.value === value)
        ? chips
        : [...chips, { key: label, value }]
    );
  }, []);

  // Free text, like typing the value: a clicked peer address matches whole.
  const filterByPeer = useCallback((ip: string) => {
    setAttributeChips((chips) =>
      chips.some((chip) => !chip.key && chip.value === ip) ? chips : [...chips, { key: "", value: ip }]
    );
  }, []);

  return (
    <div className="relative min-h-[500px]">
      <div className="mb-4">
        <GenericCard isCollapsible={false}>
          <div className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label className="block mb-1 text-sm font-medium text-semantic-text-primary">
                Time range
              </label>
              <LogsTimeRangePicker
                value={range}
                mode={rangeMode}
                onChange={handleRangeChange}
                onRefresh={refreshRange}
                isLoading={isFetching}
              />
            </div>
            <GenericDropdown
              items={firstOptions}
              value={selectedFirst}
              onSelect={handleSelectFirst}
              minWidthClass="min-w-0"
              widthClass={FILTER_ROW_INSTANCE_WIDTH}
              maxWidthClass="max-w-full"
              defaultLabel="-- Select instance --"
              label="CTX-R"
              size="sm"
              dropdownMaxHeightClass="max-h-48"
            />
            <GenericDropdown
              items={severityOptions}
              value={selectedSeverity}
              onSelect={setSelectedSeverity}
              minWidthClass="min-w-0"
              widthClass={FILTER_ROW_SEVERITY_WIDTH}
              label="Severity Level"
              size="sm"
              dropdownMaxHeightClass="max-h-48"
            />
            {/* Free text goes to the backend (searchTerm, whole range); to filter
                the loaded rows by attribute use the search above the table. */}
            <div className={FILTER_ROW_SEARCH_CLASS}>
              <label className="block mb-1 text-sm font-medium text-semantic-text-primary">
                Search Log
              </label>
              <GenericSearchbar
                size="xs"
                placeholder="Search logs"
                onFiltersChange={handleSearchLogFiltersChange}
                liveFiltering={false}
                debounceMs={200}
              />
            </div>
            <ExcludeFilter
              chips={logsView.excludes}
              onChange={logsView.setExcludes}
              className={FILTER_ROW_EXCLUDE_CLASS}
            />
          </div>
        </GenericCard>
      </div>

      {histogram && (
        <div className="mb-4">
          <LogsVolumeHistogram
            buckets={histogram}
            range={range}
            onRangeSelect={(next) => handleRangeChange(next, { kind: "custom" })}
          />
        </div>
      )}

      {/* pb-16 keeps the table's footer above the floating Archi button (h-11 + bottom-4). */}
      <div className="pb-16">
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            isFetching ? (
              <div className="p-4">
                <SkeletonTableRows
                  rows={5}
                  cols={5}
                  colWidths={["200px"]}
                  rowHeight={15}
                  rowGapClassName="space-y-3"
                />
              </div>
            ) : (
              <>
                <LogsTableToolbar
                  visibleCount={logsView.view.visibleCount}
                  hiddenHeartbeats={logsView.view.hiddenHeartbeats}
                  hideHeartbeats={logsView.hideHeartbeats}
                  onHideHeartbeatsChange={logsView.setHideHeartbeats}
                  groupRepeated={logsView.groupRepeated}
                  onGroupRepeatedChange={logsView.setGroupRepeated}
                  wrapMessages={logsView.wrapMessages}
                  onWrapMessagesChange={logsView.setWrapMessages}
                  leading={
                    <GenericSearchbar
                      size="xs"
                      placeholder="Find by attribute or value (e.g. Neighbor=10.228.175.254)"
                      suggestedKeys={suggestedKeys}
                      committedFilters={attributeChips}
                      onCommittedFiltersChange={setAttributeChips}
                      onFiltersChange={ignoreAttributeFiltersChange}
                      liveFiltering={false}
                      inputMaxWidth="100%"
                    />
                  }
                  loadMore={
                    nextCursor === null
                      ? undefined
                      : {
                          notice:
                            total === null
                              ? `Latest ${tablesData.length.toLocaleString()} in range`
                              : `${tablesData.length.toLocaleString()} of ${total.toLocaleString()} loaded`,
                          buttonLabel: `Load ${LOGS_PAGE_SIZE} more`,
                          isLoading: isLoadingMore,
                          onLoadMore: loadMore,
                          loadMoreError,
                        }
                  }
                />
                <ReflectorLogsTable<LogItem>
                  rows={logsView.view.rows}
                  wrapMessages={logsView.wrapMessages}
                  renderExpanded={(record) => (
                    <LogRowDetails
                      message={record.raw.message}
                      contextAttributes={[
                        { label: CTXR_FIELD_LABEL, value: selectedFirst },
                        { label: TIMESTAMP_FIELD_LABEL, value: record.raw.timestamp },
                      ]}
                      json={logRowJson(record.raw, selectedFirst)}
                      onFilter={filterByAttribute}
                    />
                  )}
                  onFilterPeer={filterByPeer}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                />
              </>
            )
          }
        />
      </div>
    </div>
  );
}

export default LogsPage;
